import React, { useCallback, useEffect, useMemo, useState } from 'react';
import MaterialReactTable, { type MRT_ColumnDef } from 'material-react-table';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Modal,
  Typography,
} from '@mui/material';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  fetchExportJobs,
  isExportJobActive,
  type ExportJob,
} from '../apiClient/fetchExportJobs';
import { downloadExportZip } from '../apiClient/downloadExportZip';
import { useExportJobs } from '../hooks/useExportJobs';
import { resolveExportPollIntervalMs } from '../utils/exportPollInterval';

export default function DownloadStatusModal() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { showDownloadStatusModal, closeDownloadStatusModal, invalidateExportJobs } =
    useExportJobs();
  const open = showDownloadStatusModal;

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const {
    data: apiResponse,
    isLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: ['export_jobs', 'list', pagination.pageSize, pagination.pageIndex],
    queryFn: async () => {
      const response = await fetchExportJobs({
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
      });
      setTotalCount(response.count);
      return response;
    },
    enabled: open,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchInterval: (query) => {
      if (!open) return false;
      const jobs = query.state.data?.results ?? [];
      return resolveExportPollIntervalMs(jobs, {
        shouldPoll: jobs.some(isExportJobActive),
      });
    },
  });

  const results = apiResponse?.results ?? [];

  useEffect(() => {
    if (open) {
      setPagination({ pageIndex: 0, pageSize: 10 });
      setTotalCount(0);
      refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset/refetch on open toggle
  }, [open]);

  useEffect(() => {
    if (isError && open) {
      enqueueSnackbar('Failed to load download status', {
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
      });
    }
  }, [isError, open, enqueueSnackbar]);

  const handleDownload = useCallback(
    async (job: ExportJob) => {
      if (!job.zip_filename) return;
      setDownloadingId(job.id);
      try {
        await downloadExportZip(job.zip_filename);
        invalidateExportJobs();
        void queryClient.invalidateQueries({ queryKey: ['export_jobs', 'list'] });
        refetch();
      } catch {
        enqueueSnackbar('Failed to download export file', {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      } finally {
        setDownloadingId(null);
      }
    },
    [enqueueSnackbar, invalidateExportJobs, queryClient, refetch],
  );

  const columns = useMemo<MRT_ColumnDef<ExportJob>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'Requested',
        size: 180,
        Cell: ({ cell }) => formatDateTime(cell.getValue<string>()),
      },
      {
        accessorKey: 'export_type_display',
        header: 'Export type',
        size: 160,
      },
      {
        accessorKey: 'collection_count',
        header: 'Collections',
        size: 110,
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        Cell: ({ cell }) => {
          const status = cell.getValue<ExportJob['status']>();
          return (
            <Chip
              label={formatStatusLabel(status)}
              color={statusChipColor(status)}
              size="small"
            />
          );
        },
      },
      {
        id: 'details',
        header: 'Details',
        size: 280,
        Cell: ({ row }) => {
          const job = row.original;
          if (job.status === 'failed' && job.error_summary) {
            return job.error_summary;
          }
          if (job.status === 'failed') {
            return 'Export failed.';
          }
          if (isExportJobActive(job)) {
            return (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">Preparing export…</Typography>
              </Box>
            );
          }
          if (job.status === 'completed' && job.downloaded_at) {
            return (
              <Typography variant="body2" color="text.secondary">
                Downloaded {formatDateTime(job.downloaded_at)}
              </Typography>
            );
          }
          if (job.status === 'completed') {
            return (
              <Typography variant="body2" color="text.secondary">
                Ready to download
              </Typography>
            );
          }
          return '—';
        },
      },
      {
        id: 'actions',
        header: '',
        size: 140,
        Cell: ({ row }) => {
          const job = row.original;
          if (job.status !== 'completed' || !job.zip_filename) {
            return null;
          }
          const isDownloading = downloadingId === job.id;
          return (
            <Button
              variant="contained"
              size="small"
              disabled={isDownloading}
              onClick={() => handleDownload(job)}
            >
              {isDownloading ? 'Downloading…' : 'Download'}
            </Button>
          );
        },
      },
    ],
    [downloadingId, handleDownload],
  );

  return (
    <Modal open={open} onClose={closeDownloadStatusModal}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 960,
          maxWidth: '95vw',
          maxHeight: '85vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: 3,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Typography variant="h6" component="h2" gutterBottom>
          Download Status
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Exports run in the background. Download completed files here; you may also
          receive a backup email with the same link.
        </Typography>

        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <MaterialReactTable
            columns={columns}
            data={results}
            enablePagination
            manualPagination
            rowCount={totalCount}
            onPaginationChange={setPagination}
            state={{
              pagination,
              isLoading: isLoading || isFetching,
            }}
            enableRowSelection={false}
            enableColumnFilters={false}
            enableHiding={false}
            enableDensityToggle={false}
            enableFullScreenToggle={false}
            enableSorting={false}
            enableColumnActions={false}
            enableGlobalFilter={false}
            positionToolbarAlertBanner="none"
            muiTablePaperProps={{
              sx: { boxShadow: 'none', borderRadius: 0 },
            }}
            muiTablePaginationProps={{
              rowsPerPageOptions: [10, 25, 50],
              disabled: isLoading || isFetching,
            }}
            renderTopToolbar={() => null}
          />
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Button
            variant="outlined"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Refreshing…' : 'Refresh'}
          </Button>
          <Button variant="contained" onClick={closeDownloadStatusModal}>
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

function statusChipColor(
  status: ExportJob['status'],
): 'default' | 'info' | 'warning' | 'success' | 'error' {
  switch (status) {
    case 'pending':
      return 'info';
    case 'processing':
      return 'warning';
    case 'completed':
      return 'success';
    case 'failed':
      return 'error';
    default:
      return 'default';
  }
}

function formatStatusLabel(status: ExportJob['status']): string {
  switch (status) {
    case 'pending':
      return 'Pending';
    case 'processing':
      return 'In progress';
    case 'completed':
      return 'Completed';
    case 'failed':
      return 'Failed';
    default:
      return status;
  }
}

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString();
}
